import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import "../../assets/settings.scss";
import { useNavigate } from "react-router-dom";
import {
  callAPIInterface,
  getCurrentTimeInSecond,
  updateUserAndWalletAccountSession,
} from "../constants";
import { adminPanelSettings } from "../images";
import Text from "../text/Text";
import {
  cancel,
  confirmMsg,
  confirmPassword,
  enterCode,
  otpNotReceived,
  otpWillExpireMsg,
  passwordPlaceHolder,
  resend,
  tryAgainOtpMessage,
  verification,
  verificationSubMessage,
  verifyItsYou,
} from "../messages";
import { Input } from "../input/Input";
import Button from "../button/Button";
import { useSession } from "../../hooks/useSession";
import { OTPInput } from "../otpInput/OTPInput";
import { useDispatch } from "react-redux";
import { showToast } from "../../redux/actions";
import CommonSkeleton from "../CommonSkeleton";
import { CustomLink } from "../link/Link";
import CommonHeader from "../CommonHeader";
import CustomDivider from "../divider/Divider";

function Verification() {
  const navigate = useNavigate();
  const [password, setPassword] = useState(null);
  const [buttonLoader, setButtonLoader] = useState();
  const [otp, setOtp] = useState(null);
  const [alreadySentEmail, setAlreadySentEmail] = useState(false);
  const [timerCount, setTimerCount] = useState();
  const [expiresAt, setExpiresAt] = useState();
  const [isExpiredOTP, setIsExpiredOTP] = useState(false);
  const [otpLoader, setOtpLoader] = useState(true);

  const { session } = useSession();

  const dispatch = useDispatch();

  const isLoginFromSSO = session && session?.user?.signup_method !== "email";

  const setTimerStates = (timerExpireAt) => {
    const currentSeconds = getCurrentTimeInSecond(timerExpireAt);
    setTimerCount(currentSeconds);
    setIsExpiredOTP(currentSeconds === 0);
  };

  const fetchOTPInfo = async () => {
    try {
      const response = await callAPIInterface(
        "POST",
        "/critical-operation/otp",
        {}
      );
      setAlreadySentEmail(response.email_sent);
      setExpiresAt(response?.otp_expires_at);
      setTimerStates(response?.otp_expires_at);
      setOtpLoader(false);
    } catch (err) {
      setOtpLoader(false);
      if (err?.response?.data?.type !== "TOKEN_EXPIRED") {
        dispatch(
          showToast({
            isToastOpen: true,
            toastMessage: tryAgainOtpMessage,
            toastVariant: "error",
            toastAutoHideDuration: 5000,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (isLoginFromSSO) {
      fetchOTPInfo();
    } else {
      setOtpLoader(false);
    }
  }, [isLoginFromSSO]);

  useEffect(() => {
    if (expiresAt && timerCount > 0) {
      const timer = setInterval(() => {
        setTimerStates(expiresAt);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [expiresAt, timerCount]);

  const handleCloseAccountAPI = () => {
    callAPIInterface("POST", "/close-account")
      .then(() => {
        setButtonLoader(false);
        updateUserAndWalletAccountSession();
        navigate("/settings/advance-settings/close-account/request-submitted");
      })
      .catch((_err) => {
        setButtonLoader(false);
      });
  };

  const handleConfirmPassword = () => {
    setButtonLoader(true);
    callAPIInterface("POST", "/verify-user-password", { password: password })
      .then(() => {
        handleCloseAccountAPI();
      })
      .catch((_error) => {
        setButtonLoader(false);
      });
  };

  const handleConfirmOTP = async () => {
    try {
      setButtonLoader(true);
      await callAPIInterface("POST", "/critical-operation/otp/verify", { otp });
      handleCloseAccountAPI();
    } catch (err) {
      setButtonLoader(true);
    }
  };

  const handleResendCode = () => {
    fetchOTPInfo();
  };

  return (
    <>
      <CommonHeader textAfterIcon={verification} icon="backArrow" />
      <Box className="options-container">
        <Box className="verification-wrapper">
          <Box className="module-container">
            <Box mb="40px">
              <Box textAlign="center">
                <img src={adminPanelSettings} height="54px" width="54px" />
                <Text className="default-text" size={22} font="bold">
                  {verifyItsYou}
                </Text>
                <Text className="grey-text" size={16} font="regular">
                  {otpLoader ? (
                    <Box display="flex" justifyContent="center">
                      <CommonSkeleton />
                    </Box>
                  ) : isLoginFromSSO ? (
                    `${enterCode[alreadySentEmail ? 1 : 0]} ${
                      session?.user?.email
                    }`
                  ) : (
                    verificationSubMessage
                  )}
                </Text>
              </Box>
              <Box sx={{ marginTop: "70px", marginBottom: "80px" }}>
                {isLoginFromSSO ? (
                  <>
                    <OTPInput
                      value={otp}
                      onChange={(val) => setOtp(val)}
                      numInputs={6}
                    />
                    {timerCount > 0 && (
                      <Text
                        sx={{ color: "#667085", mt: "58px" }}
                        size={14}
                        align="center"
                      >
                        {otpWillExpireMsg}{" "}
                        <Text
                          sx={{ color: "#0A0A0A" }}
                          component="span"
                          font="bold"
                        >
                          {`${Math.floor(timerCount / 60)}`.padStart(2, 0)}:
                          {`${timerCount % 60}`.padStart(2, 0)}
                        </Text>
                      </Text>
                    )}
                    {isExpiredOTP && (
                      <Text
                        className="default-text"
                        sx={{ mt: "58px" }}
                        size={14}
                        align="center"
                      >
                        {otpNotReceived}{" "}
                        <CustomLink
                          bold
                          onClick={handleResendCode}
                          size="small"
                        >
                          {resend}
                        </CustomLink>
                      </Text>
                    )}
                  </>
                ) : (
                  <Input
                    customClass="mt-16"
                    fullWidth
                    type="password"
                    isPasswordVisible={true}
                    showLabel
                    label={confirmPassword}
                    placeholder={passwordPlaceHolder}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Box>
            </Box>
            <Box>
              <CustomDivider className="settings-divider" />
              <Box
                display="flex"
                justifyContent="space-between"
                className="settings-button"
              >
                <Button
                  fullWidth
                  variant="outlined"
                  label={cancel}
                  sx={{
                    width: "45%",
                    border: "1px solid #2A67FF !important",
                    color: "#2A67FF",
                  }}
                  onClick={() => navigate(-1)}
                />
                <Button
                  fullWidth
                  loading={buttonLoader}
                  variant="contained"
                  label={confirmMsg}
                  sx={{ width: "45%" }}
                  onClick={() =>
                    isLoginFromSSO
                      ? handleConfirmOTP()
                      : handleConfirmPassword()
                  }
                  disabled={(isLoginFromSSO ? !otp : !password) || buttonLoader}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default Verification;
